import { apiClient } from './api';
import type { Restaurant } from '@/types';
import type { DeliveryProviderType } from '@/constants/deliveryProvider';

export interface CourierSettings {
  deliveryProviderType: DeliveryProviderType;
  canManageCourierSettings: boolean;
}

/**
 * RestaurantService - Restaurant management API calls
 *
 * Per Restaurant Management Panel: Restaurant CRUD operations
 * Follows pattern from catalogService.ts and orderService.ts
 */
export const restaurantService = {
  /**
   * Get restaurant for authenticated user
   */
  async getMyRestaurant(): Promise<Restaurant> {
    const response = await apiClient.get<Restaurant>('/restaurant/my');
    return response.data.data;
  },

  /**
   * Update restaurant information
   */
  async updateRestaurant(dto: Partial<Restaurant>): Promise<Restaurant> {
    const response = await apiClient.patch<Restaurant>('/restaurant/my', dto);
    return response.data.data;
  },

  /**
   * Upload logo image
   */
  async uploadLogo(file: File): Promise<{ url: string; key: string }> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post<{ url: string; key: string }>(
      '/restaurant/my/upload-logo',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );
    return response.data.data;
  },

  /**
   * Upload banner image
   */
  async uploadBanner(file: File): Promise<{ url: string; key: string }> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post<{ url: string; key: string }>(
      '/restaurant/my/upload-banner',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );
    return response.data.data;
  },

  /**
   * Toggle accepting orders status
   */
  async toggleAcceptingOrders(isAcceptingOrders: boolean): Promise<Restaurant> {
    const response = await apiClient.patch<Restaurant>('/restaurant/my/status', {
      isAcceptingOrders,
    });
    return response.data.data;
  },

  async getCourierSettings(): Promise<CourierSettings> {
    const response = await apiClient.get<CourierSettings>('/restaurant/my/courier-settings');
    return response.data.data;
  },

  async updateCourierSettings(
    deliveryProviderType: DeliveryProviderType,
  ): Promise<CourierSettings> {
    const response = await apiClient.patch<CourierSettings>(
      '/restaurant/my/courier-settings',
      { deliveryProviderType },
    );
    return response.data.data;
  },

  /**
   * Get restaurant statistics
   */
  async getStats(period: 'today' | 'week' | 'month' = 'today'): Promise<{
    restaurantId: string;
    totalOrders: number;
    totalRevenue: number;
    averageRating: number;
    reviewCount: number;
  }> {
    const response = await apiClient.get(`/restaurant/my/stats?period=${period}`);
    return response.data.data;
  },

  /**
   * Get top selling products
   */
  async getTopProducts(
    period: 'today' | 'week' | 'month' = 'today',
    limit: number = 10,
  ): Promise<
    Array<{
      productId: string | null;
      productName: string;
      totalQuantity: number;
      totalRevenue: number;
      orderCount: number;
    }>
  > {
    const response = await apiClient.get(
      `/restaurant/my/analytics/top-products?period=${period}&limit=${limit}`,
    );
    return response.data.data;
  },

  /**
   * Get revenue chart data
   */
  async getRevenueChartData(
    period: 'today' | 'week' | 'month' = 'today',
  ): Promise<Array<{ date: string; revenue: number; orders: number }>> {
    const response = await apiClient.get(
      `/restaurant/my/analytics/revenue-chart?period=${period}`,
    );
    return response.data.data;
  },

  /**
   * Update restaurant status (ONLINE, OFFLINE, BUSY, SUSPENDED)
   */
  async updateStatus(
    status: 'ONLINE' | 'OFFLINE' | 'BUSY' | 'SUSPENDED',
    duration?: number,
  ): Promise<Restaurant> {
    const response = await apiClient.patch<Restaurant>('/admin/restaurant/status', {
      status,
      duration,
    });
    return response.data.data;
  },

  /**
   * Get live analytics
   */
  async getLiveAnalytics(): Promise<{
    restaurantId: string;
    status: string;
    ordersInQueue: number;
    ordersReady: number;
    nextOrderReadyIn: number | null;
    courierEstimatedArrival: number | null;
    todayRevenue: number;
    todayOrders: number;
    averagePreparationTime: number;
    lastUpdated: string;
  }> {
    const response = await apiClient.get('/admin/restaurant/analytics/live');
    return response.data.data;
  },

  /**
   * Create modifier group
   */
  async createModifierGroup(dto: {
    name: string;
    description?: string;
    minSelection: number;
    maxSelection?: number | null;
    isRequired: boolean;
    displayOrder?: number;
    modifierOptions?: Array<{
      name: string;
      description?: string;
      priceAdjustment?: number;
      calories?: number;
      displayOrder?: number;
      isDefault?: boolean;
    }>;
  }): Promise<any> {
    const response = await apiClient.post('/admin/restaurant/menu/modifier-group', dto);
    return response.data.data;
  },

  /**
   * Duplicate modifier group
   */
  async duplicateModifierGroup(
    modifierGroupId: string,
    newName?: string,
  ): Promise<any> {
    const response = await apiClient.post(
      `/admin/restaurant/menu/modifier-group/${modifierGroupId}/duplicate`,
      { newName },
    );
    return response.data.data;
  },

  /**
   * Get restaurant working hours
   */
  async getWorkingHours(): Promise<
    Array<{
      id: string;
      dayOfWeek: number;
      startTime: string;
      endTime: string;
      restaurantId: string;
      createdAt: string;
      updatedAt: string;
    }>
  > {
    const response = await apiClient.get('/restaurant/my/hours');
    return response.data.data;
  },

  /**
   * Update restaurant working hours
   */
  async updateWorkingHours(
    hours: Array<{
      dayOfWeek: number;
      startTime: string;
      endTime: string;
    }>,
  ): Promise<
    Array<{
      id: string;
      dayOfWeek: number;
      startTime: string;
      endTime: string;
      restaurantId: string;
      createdAt: string;
      updatedAt: string;
    }>
  > {
    const response = await apiClient.patch('/restaurant/my/hours', { hours });
    return response.data.data;
  },
};
